import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, debounceTime, distinctUntilChanged, filter, map, mergeMap, switchMap, takeUntil } from 'rxjs/operators';
import { of } from 'rxjs';
import * as IngredientActions from './ingredient.actions';
import { IngredientService } from '../../services/ingredient.service';
import { NotificationService } from '../../services/notification.service';
import { TranslateService } from '@ngx-translate/core';
import { searchIngredientsBrief, searchIngredientsBriefFailure, searchIngredientsBriefSuccess, updateIngredientSearchTerm } from './ingredient.actions';

export const loadIngredients$ = createEffect(
  (
    actions$ = inject(Actions),
    ingredientService = inject(IngredientService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(IngredientActions.loadIngredients),
      mergeMap(() =>
        ingredientService.getIngredients().pipe(
          map((ingredients) => {
            // Optional:
            // notification.success('Ingredients loaded.');
            return IngredientActions.loadIngredientsSuccess({ ingredients });
          }),
          catchError((error) => {
            notification.error(translate.instant('ingredients.notifications.loadFailure'));
            return of(IngredientActions.loadIngredientsFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const addIngredient$ = createEffect(
  (
    actions$ = inject(Actions),
    ingredientService = inject(IngredientService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(IngredientActions.addIngredient),
      mergeMap(({ ingredient }) =>
        ingredientService.addIngredient(ingredient).pipe(
          map((created) => {
            notification.success(translate.instant('ingredients.notifications.addSuccess'));
            return IngredientActions.addIngredientSuccess({ ingredient: created });
          }),
          catchError((error) => {
            notification.error(translate.instant('ingredients.notifications.addFailure'));
            return of(IngredientActions.addIngredientFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const updateIngredient$ = createEffect(
  (
    actions$ = inject(Actions),
    ingredientService = inject(IngredientService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(IngredientActions.updateIngredient),
      mergeMap(({ ingredient }) =>
        ingredientService.updateIngredient(ingredient).pipe(
          map((updated) => {
            notification.success(translate.instant('ingredients.notifications.updateSuccess'));
            return IngredientActions.updateIngredientSuccess({ ingredient: updated });
          }),
          catchError((error) => {
            notification.error(translate.instant('ingredients.notifications.updateFailure'));
            return of(IngredientActions.updateIngredientFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const deleteIngredient$ = createEffect(
  (
    actions$ = inject(Actions),
    ingredientService = inject(IngredientService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(IngredientActions.deleteIngredient),
      mergeMap(({ ingredientId }) =>
        ingredientService.deleteIngredient(ingredientId).pipe(
          map(() => {
            notification.success(translate.instant('ingredients.notifications.deleteSuccess'));
            return IngredientActions.deleteIngredientSuccess({ ingredientId });
          }),
          catchError((error) => {
            notification.error(translate.instant('ingredients.notifications.deleteFailure'));
            return of(IngredientActions.deleteIngredientFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

/* 
export const loadIngredientsSuccess$ = createEffect(
  (
    actions$ = inject(Actions),
    storageStore = inject(Store<StorageState>),
    shoppingListStore = inject(Store<ShoppingListState>),
  ) =>
    actions$.pipe(
      ofType(IngredientActions.loadIngredientsSuccess),
      tap(() => {
        storageStore.dispatch(loadStorageItems());
        shoppingListStore.dispatch(loadShoppingListItems())
      })
    ),
  { functional: true, dispatch: false }
);
*/

export const searchIngredientsBrief$ = createEffect(
  (
    actions$ = inject(Actions),
    ingredientService = inject(IngredientService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(updateIngredientSearchTerm, searchIngredientsBrief),
      map(a => 'q' in a ? a.q : a.term),
      map(q => (q ?? '').trim()),
      debounceTime(250),
      distinctUntilChanged(),
      filter(q => q.length >= 2),
      switchMap(q =>
        ingredientService.searchIngredientsBrief(q, 20).pipe(
          // cancel if user clears/closes
          takeUntil(actions$.pipe(
            ofType(IngredientActions.resetIngredientSearchTerm, IngredientActions.resetIngredientsBrief)
          )),
          map(results => searchIngredientsBriefSuccess({ results })),
          catchError(error => {
            // Optional toast:
            notification.error(translate.instant('ingredients.notifications.searchFailure'));
            return of(searchIngredientsBriefFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);

export const loadEssentialIngredientsBrief$ = createEffect(
  (
    actions$ = inject(Actions),
    ingredientService = inject(IngredientService),
    notification = inject(NotificationService),
    translate = inject(TranslateService)
  ) =>
    actions$.pipe(
      ofType(IngredientActions.loadEssentialIngredientsBrief),
      switchMap(() =>
        ingredientService.getEssentialIngredientsBrief().pipe(
          map(results =>
            IngredientActions.loadEssentialIngredientsBriefSuccess({ results })
          ),
          catchError(error => {
            notification.error(translate.instant('ingredients.notifications.essentialsFailure'));
            return of(IngredientActions.loadEssentialIngredientsBriefFailure({ error }));
          })
        )
      )
    ),
  { functional: true }
);


export const IngredientEffects = {
  loadIngredients$,
  addIngredient$,
  updateIngredient$,
  deleteIngredient$,
  searchIngredientsBrief$,
  loadEssentialIngredientsBrief$
  //loadIngredientsSuccess$
};